
import { Company } from '../interface/company';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { first, map } from 'rxjs/operators';
import { CompanyUtilities } from '../class/company-utilities';
import { DataService } from './data.service';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { firstValueFrom } from 'rxjs';
import { Utilities } from '../class/utilities';
import { IInforcarQueriesResponse, IPaginatedResponse } from '../interface/inforcar-queries-response';
import { ObjectId } from '../shared/type-aliases/object-id';
import { IsoString } from '../shared/type-aliases/iso-string';
import { LOG_REPORTER_KEYS, LogReporterService } from './log-reporter.service';
import { BOOLEAN_TWO } from '@app/shared/common-enums/boolean-two';
import { FbImageDownloaderService, FIREBASE_STORAGE_PATHS } from './fb-image-downloader.service';

interface TCarKey {
  id: ObjectId,
  description: string,
  key: string,
  ip: Array<string>,
  createdAt: IsoString,
  updatedAt: IsoString
}

@Injectable({
  providedIn: 'root'
})
export class CompanyService {
  private tokenCnpjAPI = '19180|t54mDDOJK4CLF4XHEOE9dIqLaaWcqTJA';
  private loggerService = inject(LogReporterService);
  private _fbImageDownloaderService = inject(FbImageDownloaderService);
  constructor(
    private http: HttpClient,
    private storage: AngularFireStorage,
    private dataService: DataService,
  ) { }

  async get(id: string, force = false): Promise<Company> {
    try {
      if (!force && this.dataService.user) {
        const owns = await firstValueFrom(this.dataService.user.ownCompanies$);
        const linkeds = await firstValueFrom(this.dataService.user.ownCompanies$);
        const company = owns.concat(linkeds).find(c => c.id === id);
        if (company) {
          return company;
        }
      }
      const url = `${environment.mkgoURL}/api/v1/companies/${id}`;
      const response = await firstValueFrom(this.http.get<{ company: Company }>(url));
      return CompanyUtilities.complyApp(response.company);
    } catch (error) {
      console.error(error);
      this.loggerService.errorLogger(`CompanyService.get("${id}", ${force})`, LOG_REPORTER_KEYS.INVOICE).call(this, error);
    }
  }

  /**
   * @param force If you want full data, set it to true
   */
  async getByCnpj(cnpj: string, force = false): Promise<Company> {
    let company: Company;
    if (this.dataService.user) {
      const owns = await firstValueFrom(this.dataService.user.ownCompanies$);
      const linkeds = await firstValueFrom(this.dataService.user.ownCompanies$);
      company = owns.concat(linkeds).find(c => Utilities.equalUnmasked(c.cnpj, cnpj))
      if (!force) {
        return company;
      }
    }
    const unmasked = Utilities.removeMask(cnpj);
    if (!unmasked) {
      throw new Error(`CNPJ não informado: "${cnpj}"`)
    }
    const url = `${environment.mkgoURL}/api/v1/companies/cnpj/${unmasked}`;
    company = await firstValueFrom(this.http.get<{ company: Company }>(url).pipe(first(), map(r => r.company)));
    if (!force) {
      return company
    }
    const fullCompany = await this.get(company.id, true);
    return fullCompany;
  }

  /** find current user companies */
  async getAll(search?: string): Promise<Company[]> {
    let url = `${environment.mkgoURL}/api/v1/companies/user`;
    if (search) {
      url += `?search=${encodeURIComponent(search)}`;
    }
    const response: any = await this.http.get(url).pipe(first()).toPromise();
    (response.companies as Company[]).forEach(company => CompanyUtilities.complyApp(company));
    return (response.companies as Company[]).reverse();
  }

  async getBranches(): Promise<Company[]> {
    const url = `${environment.mkgoURL}/api/v1/companies/branches`;
    const options = await firstValueFrom(this.dataService.httpOptions(false));
    const response: any = await firstValueFrom(this.http.get(url, options));
    return response.companies;
  }

  async add(company: Company): Promise<void> {
    const apiCompany = CompanyUtilities.complyApi(company);
    apiCompany['cashier'] = false;
    const url = `${environment.mkgoURL}/api/v1/companies`;
    const response = await firstValueFrom(this.http.post<{ id: ObjectId }>(url, apiCompany));
    company['id'] = response.id;
    if (company.companyLogoBlob) {
      await this.updateCompanyLogo(company);
    }
    if (company.associationLogoBlob) {
      await this.updateAssociationLogo(company);
    }
  }

  async update(company: Company): Promise<Company> {
    if (company.companyLogoBlob) {
      await this.updateCompanyLogo(company);
    }
    if (company.associationLogoBlob) {
      await this.updateAssociationLogo(company);
    }
    let apiCompany = CompanyUtilities.complyApi(company);
    const url = `${environment.mkgoURL}/api/v1/companies/${company.id}`;
    const header = {
      headers: new HttpHeaders()
        .append('Company-CNPJ', company.cnpj)
        .append('Content-Type', 'application/json')
    }
    return firstValueFrom(this.http.put<Company>(url, apiCompany, header));
  }

  async updateExistInIntegrateMkgoDatabase(company: Company, existInIntegrateMkgoDatabase: BOOLEAN_TWO) {
    const url = `${environment.mkgoURL}/api/v1/companies-publics/${company.id}/update-integrate-mkgo-database-status`;
    const header = await firstValueFrom(this.dataService.httpOptions(company.cnpj));
    await firstValueFrom(this.http.put(url, { existInIntegrateMkgoDatabase }, header));
  }

  public setInvoiceNumber(cnpj: string, field: "numberInvoice" | "numberGerencial" | "numberInvoiceNFC" | "numberInvoiceNFS", newValue: number) {
    const url = `${environment.mkgoURL}/api/v1/companies-publics/numberInvoice`;
    const body = { cnpj };
    Object.defineProperty(body, field, {
      value: newValue,
      enumerable: true
    });

    const headers = new HttpHeaders().append('Content-Type', 'application/json')
    return this.http.post(url, body, { headers });
  }

  updateFavorite(id: string, favorite: number): Promise<Company> {
    const url = `${environment.mkgoURL}/api/v1/companies/${id}/favorite/${favorite}`;
    return firstValueFrom(this.http.post<Company>(url, ''));
  }


  async join(body: { cnpj: string }): Promise<Company> {
    const url = `${environment.mkgoURL}/api/v1/companies/request`;
    const headers = new HttpHeaders().append("Content-Type", "application/json");
    return firstValueFrom(this.http.post<Company>(url, JSON.stringify(body), { headers }));
  }


  /** Returns how many requests from InfoCarAPI this company have contracted to use in a interval */
  async getInfocarRequestsContracted(): Promise<number> {
    const url = `${environment.mkgoURL}/api/v1/companies/general/infoCarQuery`;
    const options = await firstValueFrom(this.dataService.httpOptions(false));
    const resp = await firstValueFrom(
      this.http.get<{ _id: ObjectId, cnpj: string, infoCarQuery: number }>(url, options));
    return resp.infoCarQuery;
  }

  /** Returns how many requests of vehicle-plate this company has made using InfoCarAPI
   * @param start The start date
   * @param end The end date
  */
  async getInfocarAmountRequestsMade(start: Date, end: Date): Promise<{amount: number, contracted: number, remaining: number}> {
    const startString = start.toISOString().split('T')[0]
    const endString = end.toISOString().split('T')[0]
    const url = `${environment.mkgoURL}/api/v1/infocar?startDate=${startString}&endDate=${endString}&cnpj=true`;
    const options = await firstValueFrom(this.dataService.httpOptions(false));
    const resp = await this.http.get(url, options).pipe(first()).toPromise() as any;
    return resp;
  }

  async getInfocarRequestsMade(start: Date, end: Date, page: number = 1, limit: number = 10): Promise<IPaginatedResponse<IInforcarQueriesResponse>> {
    const startString = start.toISOString().split('T')[0]
    const endString = end.toISOString().split('T')[0]
    const url = `${environment.mkgoURL}/api/v1/infocar/cnpj?startDate=${startString}&endDate=${endString}&page=${page}&limit=${limit}`;
    const options = await firstValueFrom(this.dataService.httpOptions(false));
    const response = await firstValueFrom(this.http.get(url, options));
    return response as any;
  }

  /** Return true if current company can made requests on infoCarAPI */
  async canRequestInfoCar(): Promise<boolean> {
    let resp: boolean;
    try {
      const { contracted, amount } = await this.getInfocarAmountRequestsMade(new Date(new Date().setDate(1)), new Date());
      this.dataService.company.infoCarContracted = contracted;
      this.dataService.company.infoCarQuery = amount;
      resp = (contracted > amount);
      return resp;
    } catch (error) {
      console.warn("\nERRO AO CONSULTAR DADOS INFOCAR DA EMPRESA\n\n", error)
      return false;
    }
  }
  
  async getCompanyURL(company: Company) {
    const path = FIREBASE_STORAGE_PATHS.companyLogo({ companyId: company.id });
    return this.storage.ref(path).getDownloadURL().toPromise()
  }

  private async updateCompanyLogo(company: Company): Promise<void> {
    const path = FIREBASE_STORAGE_PATHS.companyLogo({ companyId: company.id });
    await this.storage.upload(path, company.companyLogoBlob);
    company.companyLogo = await this.storage.ref(path).getDownloadURL().toPromise();
  }

  private async updateAssociationLogo(company: Company): Promise<void> {
    const path = FIREBASE_STORAGE_PATHS.associationLogo({ companyId: company.id });
    await this.storage.upload(path, company.associationLogoBlob);
    company.associateLogo = await this.storage.ref(path).getDownloadURL().toPromise();
  }


  public getLogo(company: Pick<Company, 'id' | 'cnpj'> = this.dataService.company) {
    const path = FIREBASE_STORAGE_PATHS.companyLogo({ companyId: company.id });
    return firstValueFrom(this._fbImageDownloaderService.getBlob({ path, cnpj: company.cnpj }));
  }

  /**
   * At integrating a new Tcar-integrated company, the TCar system will need a token to comunicate
   */
  public generateIntegrationTokenFor(company: Partial<Company> & { cnpj: string }) {
    const url = `${environment.mkgoURL}/api/v1/companies/keys`;
    const body = { "description": "MKGOService-1", "ip": [] }; // default
    const headers = new HttpHeaders().append("Company-Cnpj", company.cnpj)
    return this.http.post<{ key: TCarKey }>(url, body, { headers })
      .pipe(
        first(),
        map(resp => resp.key),
        map(key => btoa(`${key.id}:${key.key}`))
      );
  }

  public async getExternalInfoCnpj(cnpj: string): Promise<any> {
    const url = `https://api.invertexto.com/v1/cnpj/${cnpj}?token=${this.tokenCnpjAPI}`;
    const resp = await this.http.get(url).pipe(first()).toPromise();
    return resp;
  }
}
